(() => {
  if (customElements.get('velvet-product')) return;
  class VelvetProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
      this.form = this.querySelector('[data-product-form]');
      this.quantityInput = this.querySelector('[data-product-quantity]');
      this.packs = [...this.querySelectorAll('[data-pack-quantity]')].sort((a, b) => +a.dataset.packQuantity - +b.dataset.packQuantity);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.dataset.currency });
      this.quantity = 1;
      this.addEventListener('click', event => this.click(event), { signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant-select]')) this.selectVariant(event.target.value, true);
        if (event.target.matches('[data-selling-plan]')) this.selectVariant(this.variant.id);
        if (event.target === this.quantityInput) this.setQuantity(event.target.value);
      }, { signal });
      this.addEventListener('input', event => {
        if (event.target === this.quantityInput && Number.isInteger(+event.target.value) && +event.target.value > 0) {
          this.quantity = this.clampQuantity(event.target.value);
          this.update();
        }
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const details = event.target.closest('details');
        if (details) details.open = true;
      }, { signal });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.setQuantity(this.quantityInput?.value || this.quantity);
        if (this.variant.available) this.addItems([{ id: this.variant.id, quantity: this.quantity }]);
      }, { signal });
      this.selectVariant(this.querySelector('[data-current-variant]').value);
      this.querySelectorAll('[data-fbt]').forEach(group => {
        const buttons = [...group.querySelectorAll('[data-fbt-add]')];
        group.querySelectorAll('[data-fbt-total]').forEach(el => { el.textContent = this.format(buttons.reduce((sum, b) => sum + +b.dataset.fbtPrice, 0)); });
        const footer = group.querySelector('[data-fbt-footer]');
        if (footer) footer.hidden = buttons.length === 0;
      });
      const target = this.querySelector('[data-main-add]');
      const sticky = this.querySelector('[data-sticky-bar]');
      if (target && sticky) {
        this.observer = new IntersectionObserver(entries => {
          const show = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom < 0;
          sticky.classList.toggle('show', show);
          sticky.inert = !show;
          sticky.setAttribute('aria-hidden', String(!show));
        });
        this.observer.observe(target);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); clearTimeout(this.successTimer); }
    format(cents) { return window.VelvetMoney ? window.VelvetMoney.format(cents) : this.money.format(cents / 100); }
    selectVariant(id, updateURL = false) {
      this.variant = { ...(this.variants.find(v => String(v.id) === String(id)) || this.variants[0]) };
      const planSelect = this.querySelector('[data-selling-plan]');
      if (planSelect) {
        const previous = planSelect.value;
        planSelect.replaceChildren(...(this.variant.plans || []).map(plan => new Option(plan.name, plan.id)));
        if ([...planSelect.options].some(option => option.value === previous)) planSelect.value = previous;
        const allocation = this.variant.plans?.find(plan => String(plan.id) === planSelect.value);
        this.plan = allocation?.id;
        if (allocation) { this.variant.price = allocation.price; this.variant.compare = allocation.compare; }
        else this.variant.available = false;
      }
      this.querySelector('[data-current-variant]').value = this.variant.id;
      if (this.variant.image) this.showImage(String(this.variant.image));
      this.setQuantity(this.quantity);
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.variant.id);
        history.replaceState({}, '', url);
      }
      this.dispatchEvent(new CustomEvent('velvet:variant-change', { detail: { id: this.variant.id } }));
    }
    clampQuantity(value) {
      const min = this.variant.min || 1, step = this.variant.increment || 1;
      const max = Math.max(min, min + Math.floor((this.variant.max - min) / step) * step);
      const valid = Number.isFinite(+value) ? Math.floor(+value) : min;
      return Math.min(max, Math.max(min, min + Math.floor((valid - min) / step) * step));
    }
    setQuantity(value) {
      this.success = false;
      this.quantity = this.clampQuantity(value);
      if (this.quantityInput) {
        this.quantityInput.value = this.quantity;
        this.quantityInput.min = this.variant.min;
        this.quantityInput.step = this.variant.increment;
        this.quantityInput.max = Math.max(this.variant.min, this.variant.max);
      }
      this.update();
    }
    total(quantity) {
      let discount = 0;
      if (this.dataset.discounts === 'true') {
        for (const pack of this.packs) if (quantity >= +pack.dataset.packQuantity) discount = +pack.dataset.packDiscount;
      }
      // Match Shopify's per-unit percentage rounding before multiplying units.
      const step = Number(this.dataset.moneyStep) || 1;
      const unitPrice = this.variant.price - Math.floor(this.variant.price * discount / (100 * step)) * step;
      return unitPrice * quantity;
    }
    update() {
      const total = this.total(this.quantity), available = this.variant.available && this.variant.max >= this.variant.min;
      this.querySelectorAll('[data-product-price], [data-add-total]').forEach(el => { el.textContent = this.format(total); });
      this.querySelectorAll('[data-price-block]').forEach(block => {
        const preferences = block.querySelector('[data-show-compare]');
        const originalTotal = Math.max(this.variant.compare || 0, this.variant.price) * this.quantity;
        const onSale = originalTotal > total;
        const compare = block.querySelector('[data-product-compare]');
        compare.hidden = !onSale || preferences.dataset.showCompare !== 'true';
        compare.textContent = this.format(originalTotal);
        const save = block.querySelector('[data-product-saving]');
        save.hidden = !onSale || preferences.dataset.showSaving !== 'true';
        save.textContent = `${block.dataset.saveLabel} ${this.format(originalTotal - total)}`;
      });
      let selected = null;
      for (const pack of this.packs) {
        const quantity = +pack.dataset.packQuantity, active = quantity === this.quantity;
        pack.classList.toggle('active', active);
        pack.setAttribute('aria-pressed', String(active));
        pack.disabled = !available || this.clampQuantity(quantity) !== quantity || this.busy;
        pack.querySelector('[data-pack-price]').textContent = this.format(this.total(quantity));
        pack.querySelector('[data-pack-weight]').textContent = this.variant.weight || this.dataset.unit;
        pack.querySelector('[data-pack-savings]').hidden = this.dataset.discounts !== 'true' || +pack.dataset.packDiscount === 0;
        if (active) selected = pack.dataset.packLabel;
      }
      const bundle = this.querySelector('[data-bundle-unit]');
      const selection = bundle
        ? `${this.quantity} ${this.quantity === 1 ? bundle.dataset.bundleUnit : bundle.dataset.bundleUnits}`
        : selected || `${this.quantity} × ${this.variant.weight || this.dataset.unit}`;
      this.querySelectorAll('[data-pack-prompt]').forEach(el => { el.textContent = `${selection} ${el.dataset.selectedLabel}`; });
      this.querySelectorAll('[data-sticky-selection]').forEach(el => { el.textContent = selection; });
      this.querySelectorAll('[data-main-add], [data-sticky-add]').forEach(button => {
        button.disabled = !available || this.busy;
        const label = button.querySelector('[data-add-label]');
        if (label) label.textContent = !available ? this.dataset.soldOut : this.busy ? this.dataset.adding : this.success ? this.dataset.added : button.dataset.label;
      });
      this.querySelectorAll('[data-buy-now]').forEach(link => {
        link.href = `${this.dataset.root}cart/${this.variant.id}:${this.quantity}`;
        link.setAttribute('aria-disabled', String(!available || !!this.busy));
      });
      this.querySelectorAll('[data-shipping-goal]').forEach(goal => {
        window.VelvetShipping.update(goal, total);
      });
    }
    showImage(id) {
      const slides = [...this.querySelectorAll('[data-image-id]')];
      if (!slides.some(el => el.dataset.imageId === id)) return;
      slides.forEach(el => { el.hidden = el.dataset.imageId !== id; });
      this.querySelectorAll('[data-thumbnail]').forEach(el => {
        const active = el.dataset.thumbnail === id;
        el.classList.toggle('active', active);
        el.setAttribute('aria-pressed', String(active));
      });
    }
    click(event) {
      const target = event.target.closest('button, [data-buy-now]');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-thumbnail]')) this.showImage(target.dataset.thumbnail);
      if (target.matches('[data-gallery-step]')) {
        const slides = [...this.querySelectorAll('[data-image-id]')];
        const current = slides.findIndex(el => !el.hidden);
        if (slides.length) this.showImage(slides[(current + +target.dataset.galleryStep + slides.length) % slides.length].dataset.imageId);
      }
      if (target.matches('[data-pack-quantity]')) this.setQuantity(target.dataset.packQuantity);
      if (target.matches('[data-quantity-step]')) this.setQuantity(this.quantity + +target.dataset.quantityStep * this.variant.increment);
      if (target.matches('[data-sticky-add]')) this.form.requestSubmit();
      if (target.matches('[data-buy-now]')) {
        this.setQuantity(this.quantityInput?.value || this.quantity);
        if (target.getAttribute('aria-disabled') === 'true') event.preventDefault();
        else if (this.plan) { event.preventDefault(); this.addItems([{ id: this.variant.id, quantity: this.quantity }], true); }
      }
      if (target.matches('[data-fbt-add]')) this.addItems([{ id: +target.dataset.fbtAdd, quantity: 1 }]);
      if (target.matches('[data-fbt-all]')) this.addItems([...target.closest('[data-fbt]').querySelectorAll('[data-fbt-add]')].map(el => ({ id: +el.dataset.fbtAdd, quantity: 1 })));
    }
    async addItems(items, checkout = false) {
      if (this.busy || !items.length) return;
      this.busy = true;
      items = items.map(item => item.id === this.variant.id && this.plan ? { ...item, selling_plan: this.plan } : item);
      const error = this.querySelector('.vp-purchase-error');
      error.hidden = true;
      const buttons = [...this.querySelectorAll('[data-fbt-add], [data-fbt-all]')];
      buttons.forEach(b => { b.disabled = true; });
      this.update();
      try {
        let result;
        if (window.VelvetCart) {
          result = await window.VelvetCart.add(items, { open: !checkout, opener: document.activeElement });
        } else {
          const response = await fetch(`${this.dataset.root}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
          });
          result = await response.json();
          if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        }
        if (checkout) { location.assign(`${this.dataset.root}checkout`); return; }
        this.success = true;
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => { this.success = false; this.update(); }, 4000);
        this.querySelector('[data-product-status]').textContent = this.dataset.added;
        // Refresh the actual cart count; a failed count refresh must not imply the add failed.
        if (!window.VelvetCart) try {
          const cartResponse = await fetch(`${this.dataset.root}cart.js`, { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
          }
        } catch { /* The successful add remains valid if the count refresh is offline. */ }
      } catch (reason) {
        error.textContent = reason.message || this.dataset.error;
        error.hidden = false;
        error.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } finally {
        this.busy = false;
        buttons.forEach(b => { b.disabled = false; });
        this.update();
      }
    }
  }
  customElements.define('velvet-product', VelvetProduct);
})();
